import {Inject, Injectable, Self} from '@angular/core';
import {firstValueFrom, map} from 'rxjs';
import {CoreResponse} from '@models/app/response/CoreResponse';
import {HttpClient, HttpContext, HttpHeaders, HttpParams} from '@angular/common/http';
import {mapRes} from '@shared/utils/data-parsing/http/ResponseMapper.util';
import {objectToQueryParams} from '@shared/utils/data-parsing/http/ObjectToQueryParams';
import {Observable} from 'rxjs';
import {TenantService} from '@services/core/tenant/tenant.service';
import {hasValue} from '@shared/utils/helpers';
import {RegionService} from "@app/areas/auth/services/region.service";
import { REGION_CTX } from '@src/app/areas/auth/injectables/region-token';

interface RequestOpts {
  headers?: HttpHeaders | Record<string, string | string[]>;
  context?: HttpContext;
  observe?: 'body';
  params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;
  reportProgress?: boolean;
  withCredentials?: boolean;
  transferCache?: {
    includeHeaders?: string[];
  } | boolean;
}
@Injectable({
  providedIn: 'root'
})
export class RequestService {
  constructor(
    private http: HttpClient,
    private regionSvc: RegionService,
    private tenantService: TenantService,
  ) {
  }

  private applyRegionContext(opts: RequestOpts): RequestOpts {
    const region = this.regionSvc.selectedRegion$()
    if (hasValue(region)) {
      const ctx = new HttpContext().set(REGION_CTX, region);
      opts = {...opts, context: ctx}
    }

    return opts
  }

  public get<T = any>(url: string, queryParams: object = null, withSelectedTenantId: boolean = true, reqOpts: RequestOpts | null = null): Observable<CoreResponse<T>> {
    let opts: RequestOpts = withSelectedTenantId ? this.tenantHeaderOptionsStructure : null;
    opts = this.applyRegionContext(opts)
    return this.makeGetRequest(url, queryParams, {...opts, ...reqOpts});
  }

  public getFile(url: string): Observable<Blob> {
    // Ensure the response type is set to 'blob' to handle binary data
    const finalUrl = `${url}`;
    const opts = this.applyRegionContext({})
    return this.http.get(finalUrl, { responseType: 'blob', ...opts });
  }

  public getFileWithHeaders(url: string)
    : Observable<{ blob: Blob; fileName?: string; contentType?: string }> {
    // only carry over the HttpContext (region), avoid `observe`/`responseType` clashes
    const { context } = this.applyRegionContext({});

    return this.http.get(url, {
      observe: 'response' as const,
      responseType: 'blob' as const,
      context
    }).pipe(
      map((res) => {
        const contentType = res.headers.get('content-type') ?? undefined;
        const cd = res.headers.get('content-disposition') ?? '';
        const m =
          /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd) ||
          /filename=["']?([^"']+)["']?/i.exec(cd);
        const fileName = m ? decodeURIComponent(m[1]) : undefined;
        return { blob: res.body!, fileName, contentType };
      })
    );
  }

  public post(url: string, body: object = null, withSelectedTenantId = true): Observable<CoreResponse> {
    let opts: RequestOpts = withSelectedTenantId ? this.tenantHeaderOptionsStructure : null;
    opts = this.applyRegionContext(opts)
    return this.makePostRequest(url, body, opts);
  }

  public postFile(
    url: string,
    file: File,
    withSelectedTenantId = true
  ): Observable<CoreResponse> {
    let opts: RequestOpts = withSelectedTenantId ? this.tenantHeaderOptionsStructure : {};
    opts = this.applyRegionContext(opts)

    // Create a FormData object to send the file
    const formData = new FormData();
    formData.append('file', file);

    // Do not manually set Content-Type; it will be handled by the browser
    // if (opts?.headers) {
    //   opts.headers = opts.headers.delete('Content-Type');
    // }

    return this.makePostRequest(url, formData, opts);
  }

  public put(url: string, body: object = null, withSelectedTenantId = true): Observable<CoreResponse> {
    let opts: RequestOpts = withSelectedTenantId ? this.tenantHeaderOptionsStructure : null;
    opts = this.applyRegionContext(opts)
    return this.makePutRequest(url, body, opts).pipe(mapRes);
  }

  public delete(url: string, withSelectedTenantId = true): Observable<CoreResponse> {
    let opts: RequestOpts = withSelectedTenantId ? this.tenantHeaderOptionsStructure : null;
    opts = this.applyRegionContext(opts)
    return this.makeDeleteRequest(url, opts);
  }

  public async getAsync(url: string, queryParams: object = null): Promise<CoreResponse> {
    const finalUrl = `${url}${objectToQueryParams(queryParams)}`;

    return await firstValueFrom(this.get(finalUrl).pipe(mapRes));
  }

  public async postAsync(
    url: string,
    formData: object = null,
    queryParams: object = null,
    withSelectedTenantId = true
  ): Promise<CoreResponse> {
    const finalUrl = `${url}${objectToQueryParams(queryParams)}`;
    return await firstValueFrom(this.post(finalUrl, formData, withSelectedTenantId));
  }

  public async putAsync(
    url: string,
    formData: object = null,
    queryParams: object = null,
    withSelectedTenantId = true
  ): Promise<CoreResponse> {
    const finalUrl = `${url}${objectToQueryParams(queryParams)}`;
    return await firstValueFrom(this.put(finalUrl, formData, withSelectedTenantId));
  }

  private get tenantHeaderOptionsStructure(): { headers?: HttpHeaders } {
    const selectedTenant = this.tenantService.selectedTenantValue;

    let tenantId: string = null;
    if (hasValue(selectedTenant)) {
      tenantId = selectedTenant.id;
    }

    return hasValue(tenantId) ? {
      headers: new HttpHeaders({'X-Tenant-Id': tenantId})
    } : {};
  }

  public async deleteAsync(
    url: string,
    queryParams: object = null,
    withSelectedTenantId = true
  ): Promise<CoreResponse> {
    const finalUrl = `${url}${objectToQueryParams(queryParams)}`;
    return await firstValueFrom(this.delete(finalUrl, withSelectedTenantId));
  }

  private makeGetRequest<T = any>(url: string, queryParams: object = null, opts: any = {}): Observable<CoreResponse<T>> {
    const finalUrl = `${url}${objectToQueryParams(queryParams)}`;
    return this.http.get(finalUrl, opts).pipe(mapRes);
  }

  private makeDeleteRequest<T = any>(url: string, opts: any = {}): Observable<CoreResponse<T>> {
    return this.http.delete(url, opts).pipe(mapRes);
  }

  private makePutRequest<T = any>(url: string, body: object = null, opts: any = {}): Observable<CoreResponse<T>> {
    return this.http.put(url, body, opts).pipe(mapRes);
  }

  private makePostRequest<T = any>(url: string, body: object = null, opts: any = {}): Observable<CoreResponse<T>> {
    return this.http.post(url, body, opts).pipe(mapRes);
  }
}
